<template>
  <div class="gaode-location-picker">
    <!-- 地址搜索 -->
    <div class="search-area">
      <el-input
        v-model="searchKeyword"
        placeholder="请输入地址进行搜索"
        clearable
        @input="handleSearchInput"
        @clear="clearSearchResults"
      >
        <template #append>
          <el-button @click="handleSearch" :loading="searching">搜索</el-button>
        </template>
      </el-input>
    </div>

    <!-- 搜索结果 -->
    <div v-if="searchResults.length > 0" class="search-results">
      <div class="result-list">
        <div
          v-for="result in searchResults"
          :key="result.id"
          class="result-item"
          @click.stop="selectLocation(result)"
        >
          <div class="result-title">{{ result.name }}</div>
          <div class="result-address">{{ result.address }}</div>
        </div>
      </div>
    </div>

    <!-- 地图容器 -->
    <div class="map-container">
      <el-amap
        class="amap-view"
        :center="mapCenter"
        :zoom="zoom"
        :scrollWheel="true"
        @complete="mapReady"
        @click="mapClick"
      >
        <!-- 当前标记点 -->
        <el-amap-marker
          v-if="selectedLocation"
          :position="selectedLocation.position"
          :draggable="true"
          @dragend="markerDragend"
        >
          <el-amap-info-window
            :position="selectedLocation.position"
            :visible="true"
          >
            {{ selectedLocation.address }}
          </el-amap-info-window>
        </el-amap-marker>

        <!-- 搜索结果标记 -->
        <el-amap-marker
          v-for="result in searchResults"
          :key="result.id"
          :position="result.position"
          @click="selectLocation(result)"
        />

        <!-- 定位控件 -->
        <el-amap-geolocation
          :enableHighAccuracy="true"
          :timeout="10000"
          :buttonOffset="[10, 10]"
          :buttonPosition="'RB'"
          @complete="geolocationComplete"
        />
      </el-amap>
    </div>

    <!-- 选中的地址信息 -->
    <div v-if="selectedLocation" class="selected-info">
      <el-alert
        :title="`已选择位置：${selectedLocation.address}`"
        type="success"
        :closable="false"
        show-icon
      />
    </div>

    <!-- 操作按钮 -->
    <div class="action-buttons">
      <el-button @click="handleCancel">取消</el-button>
      <el-button type="primary" @click="handleConfirm" :disabled="!selectedLocation">
        确认选择
      </el-button>
    </div>
  </div>
</template>

<script>
import { ElMessage } from 'element-plus'

export default {
  name: 'GaodeLocationPicker',
  props: {
    modelValue: {
      type: String,
      default: ''
    }
  },
  emits: ['update:modelValue', 'confirm', 'cancel'],
  data() {
    return {
      map: null,
      searchKeyword: '',
      searchResults: [],
      searching: false,
      selectedLocation: null,
      mapCenter: [116.397428, 39.90923], // 北京中心点
      zoom: 15,
      geocoder: null,
      placeSearch: null
    }
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(newVal) {
        if (newVal && this.map) {
          this.geocodeAddress(newVal)
        }
      }
    }
  },
  methods: {
    // 地图初始化完成
    mapReady(map) {
      this.map = map
      
      // 初始化地理编码服务
      this.geocoder = new AMap.Geocoder({
        city: '全国'
      })
      
      // 初始化地点搜索服务
      this.placeSearch = new AMap.PlaceSearch({
        city: '全国',
        map: this.map,
        panel: false
      })
      
      // 如果有初始值，进行地理编码
      if (this.modelValue) {
        this.geocodeAddress(this.modelValue)
      }
    },

    // 地址搜索
    async handleSearch() {
      if (!this.searchKeyword.trim()) return

      this.searching = true
      try {
        // 使用高德地图的搜索服务
        const placeSearch = new AMap.PlaceSearch({
          city: '全国',
          pageSize: 10,
          pageIndex: 1
        })
        
        placeSearch.search(this.searchKeyword, (status, result) => {
          this.searching = false
          console.log('高德地图搜索状态:', status, '搜索结果:', result)
          
          if (status === 'complete' && result.poiList && result.poiList.pois) {
            this.searchResults = result.poiList.pois.map((poi, index) => ({
              id: poi.id || `poi-${index}-${Date.now()}`,
              name: poi.name,
              address: poi.address || poi.name,
              position: [poi.location.lng, poi.location.lat]
            }))
            
            if (this.searchResults.length === 0) {
              ElMessage.warning('未找到相关地址，请尝试其他关键词')
            } else {
              ElMessage.success(`找到 ${this.searchResults.length} 个相关地址`)
            }
          } else {
            this.searchResults = []
            ElMessage.error('搜索失败，请检查网络连接或关键词')
          }
        })
      } catch (error) {
        this.searching = false
        console.error('搜索地址失败:', error)
        ElMessage.error('搜索地址失败，请检查高德地图配置')
      }
    },

    // 搜索输入处理
    handleSearchInput() {
      // 防抖搜索
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => {
        if (this.searchKeyword.trim()) {
          this.handleSearch()
        } else {
          this.clearSearchResults()
        }
      }, 500)
    },

    // 清除搜索结果
    clearSearchResults() {
      this.searchResults = []
    },

    // 选择位置
    selectLocation(location) {
      try {
        if (!location || !location.position) {
          console.error('无效的位置数据:', location)
          return
        }
        
        // 使用setTimeout避免在事件处理中直接操作DOM
        setTimeout(() => {
          this.selectedLocation = {
            ...location,
            address: location.address || location.name || '未知地址'
          }
          this.searchKeyword = this.selectedLocation.address
          this.mapCenter = location.position
          this.clearSearchResults()
        }, 0)
      } catch (error) {
        console.error('选择位置时发生错误:', error)
        ElMessage.error('选择位置失败，请重试')
      }
    },

    // 地图点击事件
    mapClick(e) {
      if (!this.geocoder) return

      const position = [e.lnglat.lng, e.lnglat.lat]
      this.reverseGeocode(position)
    },

    // 标记点拖拽结束
    markerDragend(e) {
      const position = [e.lnglat.lng, e.lnglat.lat]
      this.reverseGeocode(position)
    },

    // 反向地理编码
    reverseGeocode(position) {
      if (!this.geocoder) return

      this.geocoder.getAddress(position, (status, result) => {
        if (status === 'complete' && result.regeocode) {
          this.selectedLocation = {
            address: result.regeocode.formattedAddress,
            position: position
          }
          this.searchKeyword = result.regeocode.formattedAddress
        }
      })
    },

    // 地理编码
    geocodeAddress(address) {
      if (!this.geocoder) return

      this.geocoder.getLocation(address, (status, result) => {
        if (status === 'complete' && result.geocodes.length > 0) {
          const location = result.geocodes[0]
          this.selectedLocation = {
            address: address,
            position: [location.location.lng, location.location.lat]
          }
          this.mapCenter = this.selectedLocation.position
        }
      })
    },

    // 定位完成
    geolocationComplete(result) {
      if (result.position) {
        this.mapCenter = [result.position.lng, result.position.lat]
        this.reverseGeocode(this.mapCenter)
      }
    },

    // 确认选择
    handleConfirm() {
      if (this.selectedLocation) {
        this.$emit('update:modelValue', this.selectedLocation.address)
        this.$emit('confirm', this.selectedLocation)
      }
    },

    // 取消选择
    handleCancel() {
      this.$emit('cancel')
    }
  }
}
</script>

<style scoped>
.gaode-location-picker {
  width: 100%;
  height: 500px;
  display: flex;
  flex-direction: column;
}

.search-area {
  margin-bottom: 10px;
}

.search-results {
  max-height: 150px;
  overflow-y: auto;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  margin-bottom: 10px;
}

.result-list {
  background: white;
}

.result-item {
  padding: 8px 12px;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
}

.result-item:hover {
  background-color: #f5f7fa;
}

.result-item:last-child {
  border-bottom: none;
}

.result-title {
  font-weight: bold;
  font-size: 14px;
  margin-bottom: 4px;
}

.result-address {
  font-size: 12px;
  color: #909399;
}

.map-container {
  flex: 1;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  overflow: hidden;
}

.amap-view {
  width: 100%;
  height: 100%;
}

.selected-info {
  margin: 10px 0;
}

.action-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 10px;
}
</style>
